import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";
import {
  useDeliverApi,
  useDeliverFetchClient,
  type DeliverFetchClient,
} from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

import { formatCount } from "../-data/format";
import { DRAWER_PAGE_SIZE } from "../-data/filters";
import type { ExportFormat } from "../-data/download";
import { jsonBody } from "../-data/json-body";
import { useFileExport } from "../-data/use-file-export";

import { ExportLinks } from "./export-links";
import { PagedFrame } from "./paged-frame";
import { ScanBar } from "./scan-bar";
import styles from "./route-drawer.module.css";

export type TabProps = {
  warehouse: number;
  routeId: string;
  /** Narrows the tab to one stop, where the endpoint takes one. */
  stop?: number;
  lang: string;
  t: TFunction;
};

/** One page of a tab: which page, and a way to move. */
function usePaging() {
  const [page, setPage] = useState(0);
  return { page, setPage };
}

/** The tabs whose endpoint can also answer with a file. */
type ExportSegment = "items" | "pieces" | "returns" | "proof";

/**
 * The tab's current page as a file: the same route, warehouse, page, size and
 * stop the table is showing, so the file matches the screen. One call per
 * endpoint because each path is its own type.
 */
function fetchTabFile(
  api: DeliverFetchClient,
  segment: ExportSegment,
  routeId: string,
  query: {
    warehouse: number;
    page: number;
    size: number;
    stop?: number;
    format: ExportFormat;
  },
) {
  const options = {
    params: { path: { routeId }, query },
    parseAs: "blob" as const,
  };
  switch (segment) {
    case "items":
      return api.GET("/app/deliver/api/routes/{routeId}/items", options);
    case "pieces":
      return api.GET("/app/deliver/api/routes/{routeId}/pieces", options);
    case "returns":
      return api.GET("/app/deliver/api/routes/{routeId}/returns", options);
    case "proof":
      return api.GET("/app/deliver/api/routes/{routeId}/proof", options);
  }
}

function useTabExport(props: TabProps, page: number, segment: ExportSegment) {
  const { routeId, warehouse, stop } = props;
  const api = useDeliverFetchClient();
  const fetcher = useCallback(
    (format: ExportFormat) =>
      fetchTabFile(api, segment, routeId, {
        warehouse,
        page,
        size: DRAWER_PAGE_SIZE,
        ...(stop !== undefined ? { stop } : {}),
        format,
      }),
    [api, segment, routeId, warehouse, page, stop],
  );
  return useFileExport(fetcher, "route-" + routeId + "-" + segment);
}

function useTabQuery(props: TabProps, page: number) {
  return useMemo(
    () => ({
      path: { routeId: props.routeId },
      query: {
        warehouse: props.warehouse,
        page,
        size: DRAWER_PAGE_SIZE,
      },
    }),
    [props.routeId, props.warehouse, page],
  );
}

export function StopsTab(
  props: TabProps & { onViewItems: (stop: number) => void },
) {
  const { t, lang } = props;
  const client = useDeliverApi();
  const { page, setPage } = usePaging();
  const params = useTabQuery(props, page);
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/stops",
    {
      params,
    },
  );
  const rows = jsonBody(query.data)?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="drawer-stops"
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyText={t("drawer.empty.stops")}
      page={page}
      hasMore={jsonBody(query.data)?.hasMore ?? false}
      isFetching={query.isFetching}
      onPage={setPage}
      t={t}
    >
      <div className={styles.scroll}>
        <Table aria-label={t("drawer.tabs.stops")} isResizable={false}>
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("drawer.columns.stop")}
            </TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.customer")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.status")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.started")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.finished")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.delivered")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.scanPct")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.returns")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.signature")}</TableHeaderCell>
            <TableHeaderCell>
              <span className="sr-only">{t("drawer.columns.items")}</span>
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.stop} data-test-role="stop-row">
                <TableCell>{row.stop}</TableCell>
                <TableCell>{row.customerName || none}</TableCell>
                <TableCell>
                  {row.status ? (
                    <Tag
                      label={row.status}
                      display={{
                        tone: /complet|deliver/i.test(row.status)
                          ? "success"
                          : "info",
                      }}
                    />
                  ) : (
                    none
                  )}
                </TableCell>
                <TableCell>
                  <span className={styles.nowrap}>
                    {row.startTimestamp ?? none}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.nowrap}>
                    {row.finishTimestamp ?? none}
                  </span>
                </TableCell>
                <TableCell>{n(row.piecesDelivered)}</TableCell>
                <TableCell>
                  <ScanBar value={row.scanPct} lang={lang} none={none} />
                </TableCell>
                <TableCell>{n(row.currentReturns)}</TableCell>
                <TableCell>{row.signatureName || none}</TableCell>
                <TableCell>
                  <Button
                    kind="text-only"
                    variant="tertiary"
                    scale="inline"
                    text={t("drawer.tabs.items")}
                    onPress={() => props.onViewItems(row.stop)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </PagedFrame>
  );
}

export function ItemsTab(props: TabProps) {
  const { t, lang } = props;
  const client = useDeliverApi();
  const { page, setPage } = usePaging();
  const exp = useTabExport(props, page, "items");
  const base = useTabQuery(props, page);
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/items",
    {
      params: {
        path: base.path,
        query: {
          ...base.query,
          ...(props.stop !== undefined ? { stop: props.stop } : {}),
        },
      },
    },
  );
  const rows = jsonBody(query.data)?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="drawer-items"
      toolbar={
        <ExportLinks
          onExport={exp.exportAs}
          busy={exp.busy}
          failed={exp.failed}
          t={t}
        />
      }
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyText={t("drawer.empty.items")}
      page={page}
      hasMore={jsonBody(query.data)?.hasMore ?? false}
      isFetching={query.isFetching}
      onPage={setPage}
      t={t}
    >
      <div className={styles.scroll}>
        <Table aria-label={t("drawer.tabs.items")} isResizable={false}>
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("drawer.columns.stop")}
            </TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.item")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.description")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.uom")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.operation")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.delivered")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.scanPct")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.returns")}</TableHeaderCell>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.stop}:${row.item}:${row.uom}:${row.operation}`}
                data-test-role="item-row"
              >
                <TableCell>{row.stop}</TableCell>
                <TableCell>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.uom}</TableCell>
                <TableCell>{row.operation || none}</TableCell>
                <TableCell>{n(row.piecesDelivered)}</TableCell>
                <TableCell>
                  <ScanBar value={row.scanPct} lang={lang} none={none} />
                </TableCell>
                <TableCell>{n(row.currentReturns)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </PagedFrame>
  );
}

export function PiecesTab(props: TabProps) {
  const { t, lang } = props;
  const client = useDeliverApi();
  const { page, setPage } = usePaging();
  const exp = useTabExport(props, page, "pieces");
  const base = useTabQuery(props, page);
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/pieces",
    {
      params: {
        path: base.path,
        query: {
          ...base.query,
          ...(props.stop !== undefined ? { stop: props.stop } : {}),
        },
      },
    },
  );
  const rows = jsonBody(query.data)?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="drawer-pieces"
      toolbar={
        <ExportLinks
          onExport={exp.exportAs}
          busy={exp.busy}
          failed={exp.failed}
          t={t}
        />
      }
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyText={t("drawer.empty.pieces")}
      page={page}
      hasMore={jsonBody(query.data)?.hasMore ?? false}
      isFetching={query.isFetching}
      onPage={setPage}
      t={t}
    >
      <div className={styles.scroll}>
        <Table aria-label={t("drawer.tabs.pieces")} isResizable={false}>
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("drawer.columns.stop")}
            </TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.item")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.trax")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.uom")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.operation")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.initBkdn")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.retnBkdn")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.bkdnPcs")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.bkdnRet")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.delivered")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.scanPct")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.returns")}</TableHeaderCell>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.stop}:${row.item}:${row.trax}`}
                data-test-role="piece-row"
              >
                <TableCell>{row.stop}</TableCell>
                <TableCell>{row.item}</TableCell>
                <TableCell>{n(row.trax)}</TableCell>
                <TableCell>{row.uom}</TableCell>
                <TableCell>{row.operation || none}</TableCell>
                <TableCell>{row.initialBreakdownFlag || none}</TableCell>
                <TableCell>{row.returnBreakdownFlag || none}</TableCell>
                <TableCell>{n(row.breakdownPieces)}</TableCell>
                <TableCell>{n(row.breakdownPiecesReturned)}</TableCell>
                <TableCell>{n(row.piecesDelivered)}</TableCell>
                <TableCell>
                  <ScanBar value={row.scanPct} lang={lang} none={none} />
                </TableCell>
                <TableCell>{n(row.currentReturns)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </PagedFrame>
  );
}

export function ReturnsTab(props: TabProps) {
  const { t, lang } = props;
  const client = useDeliverApi();
  const { page, setPage } = usePaging();
  const exp = useTabExport(props, page, "returns");
  const params = useTabQuery(props, page);
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/returns",
    { params },
  );
  const rows = jsonBody(query.data)?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="drawer-returns"
      toolbar={
        <ExportLinks
          onExport={exp.exportAs}
          busy={exp.busy}
          failed={exp.failed}
          t={t}
        />
      }
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyText={t("drawer.empty.returns")}
      page={page}
      hasMore={jsonBody(query.data)?.hasMore ?? false}
      isFetching={query.isFetching}
      onPage={setPage}
      t={t}
    >
      <div className={styles.scroll}>
        <Table aria-label={t("drawer.tabs.returns")} isResizable={false}>
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("drawer.columns.order")}
            </TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.customer")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.item")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.description")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.original")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.processed")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.remaining")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.returnCode")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.status")}</TableHeaderCell>
            <TableHeaderCell>{t("drawer.columns.memo")}</TableHeaderCell>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.stop}:${row.item}:${row.uom}:${row.returnCode}:${row.uniquePiece}`}
                data-test-role="return-row"
              >
                <TableCell>{row.order}</TableCell>
                <TableCell>{row.customerName || none}</TableCell>
                <TableCell>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{n(row.originalQty)}</TableCell>
                <TableCell>{n(row.processedQty)}</TableCell>
                <TableCell>{n(row.remainingQty)}</TableCell>
                <TableCell>{row.returnCodeDesc || row.returnCode}</TableCell>
                <TableCell>
                  <Tag
                    label={row.statusDesc || row.status}
                    display={{ tone: "neutral" }}
                  />
                </TableCell>
                <TableCell>{row.memo || none}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </PagedFrame>
  );
}

export function ProofTab(props: TabProps) {
  const { t } = props;
  const client = useDeliverApi();
  const { page, setPage } = usePaging();
  const exp = useTabExport(props, page, "proof");
  const base = useTabQuery(props, page);
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/proof",
    {
      params: {
        path: base.path,
        query: {
          ...base.query,
          ...(props.stop !== undefined ? { stop: props.stop } : {}),
        },
      },
    },
  );
  const rows = jsonBody(query.data)?.rows ?? [];

  return (
    <PagedFrame
      testRole="drawer-proof"
      toolbar={
        <ExportLinks
          onExport={exp.exportAs}
          busy={exp.busy}
          failed={exp.failed}
          t={t}
        />
      }
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyText={t("drawer.empty.proof")}
      page={page}
      hasMore={jsonBody(query.data)?.hasMore ?? false}
      isFetching={query.isFetching}
      onPage={setPage}
      t={t}
    >
      <ul className={styles.proofGrid}>
        {rows.map((row) => {
          const caption = t("drawer.proof.caption", {
            type: row.type,
            stop: row.stop,
          });
          return (
            <li
              key={`${row.type}:${row.stop}:${row.fileName}`}
              className={styles.proof}
              data-test-role="proof-item"
            >
              {row.url ? (
                <a
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("drawer.proof.open", { label: caption })}
                >
                  <img
                    className={styles.proofImage}
                    src={row.url}
                    alt={row.fileName}
                    loading="lazy"
                  />
                </a>
              ) : (
                <div className={styles.proofMissing}>
                  {t("drawer.proof.noImage")}
                </div>
              )}
              <span className={styles.primary}>{caption}</span>
              {row.item ? (
                <span className={styles.secondary}>{row.item}</span>
              ) : null}
              {row.fileTimestamp ? (
                <span className={styles.secondary}>{row.fileTimestamp}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </PagedFrame>
  );
}
